import { useState, useCallback } from "react";
import {
  useRateCards,
  useUploadHaulierRateCard,
  useSetWarehouseRates,
  useRateCardRates,
} from "../hooks/useRateCards";
import { rateCards } from "../services/api";
import { RateCardType, ChargeType, Haulier, HAULIER_LABELS } from "../types";
import type { RateCard, BoxBandRate, TpnRate } from "../types";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatCurrency(n: number): string {
  return `£${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatPoa(n: number | null): string {
  return n == null ? "P.O.A" : formatCurrency(n);
}

function formatDash(n: number | null): string {
  return n == null ? "—" : formatCurrency(n);
}

function cardHaulier(card: RateCard): Haulier {
  return card.haulier ?? Haulier.PALLETWAYS;
}

/** Sorts rate cards most-recent-first by effective_from, falling back to uploaded_at to break ties. */
function compareCardRecency(a: RateCard, b: RateCard): number {
  const byEffective = new Date(b.effective_from).getTime() - new Date(a.effective_from).getTime();
  if (byEffective !== 0) return byEffective;
  const aUploaded = a.uploaded_at ? new Date(a.uploaded_at).getTime() : 0;
  const bUploaded = b.uploaded_at ? new Date(b.uploaded_at).getTime() : 0;
  return bUploaded - aUploaded;
}

const HAULIER_ORDER: Haulier[] = [Haulier.PALLETWAYS, Haulier.BOX_LOGISTICS, Haulier.TPN];

const BOX_SERVICE_ORDER = ["24hr", "48hr", "72hr"];

// ─── Component ────────────────────────────────────────────────────────────────

export default function RateCardsPage() {
  const { data, isLoading, error } = useRateCards();
  const uploadHaulier = useUploadHaulierRateCard();
  const setWarehouse = useSetWarehouseRates();

  // Haulier upload form state
  const [showHaulierUpload, setShowHaulierUpload] = useState(false);
  const [haulierFile, setHaulierFile] = useState<File | null>(null);
  const [haulierEffective, setHaulierEffective] = useState("");
  const [uploadHaulierType, setUploadHaulierType] = useState<Haulier>(Haulier.PALLETWAYS);

  // Warehouse edit form state
  const [showWarehouseEdit, setShowWarehouseEdit] = useState(false);
  const [pickRate, setPickRate] = useState("");
  const [receiptRate, setReceiptRate] = useState("");
  const [rentalRate, setRentalRate] = useState("");
  const [warehouseEffective, setWarehouseEffective] = useState("");

  const allCards = data?.rate_cards ?? [];

  // Split by type — most recent first (API returns sorted by effective_from desc)
  const haulierCards = allCards.filter((c) => c.card_type === RateCardType.HAULIER);
  const warehouseCards = allCards.filter((c) => c.card_type === RateCardType.WAREHOUSE);

  const currentWarehouse = warehouseCards[0] ?? null;

  // Sort most-recent-first so "current" doesn't depend on backend ordering.
  const sortedHaulierCards = [...haulierCards].sort(compareCardRecency);

  const currentByHaulier: Record<Haulier, RateCard | null> = {
    [Haulier.PALLETWAYS]: sortedHaulierCards.find((c) => cardHaulier(c) === Haulier.PALLETWAYS) ?? null,
    [Haulier.BOX_LOGISTICS]: sortedHaulierCards.find((c) => cardHaulier(c) === Haulier.BOX_LOGISTICS) ?? null,
    [Haulier.TPN]: sortedHaulierCards.find((c) => cardHaulier(c) === Haulier.TPN) ?? null,
  };

  // Haulier upload handler
  const handleHaulierUpload = useCallback(async () => {
    if (!haulierFile || !haulierEffective) return;
    await uploadHaulier.mutateAsync({
      file: haulierFile,
      effectiveFrom: haulierEffective,
      haulier: uploadHaulierType,
    });
    setShowHaulierUpload(false);
    setHaulierFile(null);
    setHaulierEffective("");
    setUploadHaulierType(Haulier.PALLETWAYS);
  }, [haulierFile, haulierEffective, uploadHaulierType, uploadHaulier]);

  // Warehouse save handler
  const handleWarehouseSave = useCallback(async () => {
    if (!warehouseEffective) return;
    const rates = [
      { charge_type: ChargeType.PICK, unit_price: parseFloat(pickRate) || 0 },
      { charge_type: ChargeType.RECEIPT, unit_price: parseFloat(receiptRate) || 0 },
      { charge_type: ChargeType.RENTAL, unit_price: parseFloat(rentalRate) || 0 },
    ];
    await setWarehouse.mutateAsync({
      rates,
      effectiveFrom: warehouseEffective,
    });
    setShowWarehouseEdit(false);
    setPickRate("");
    setReceiptRate("");
    setRentalRate("");
    setWarehouseEffective("");
  }, [pickRate, receiptRate, rentalRate, warehouseEffective, setWarehouse]);

  // ── Loading ──
  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Rate Cards</h1>
        <div className="flex items-center justify-center py-20">
          <svg className="h-6 w-6 animate-spin text-blue-600" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="ml-2 text-sm text-gray-500">Loading rate cards...</span>
        </div>
      </div>
    );
  }

  // ── Error ──
  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Rate Cards</h1>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Failed to load rate cards. Please try refreshing the page.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Rate Cards</h1>
        <p className="mt-1 text-sm text-gray-600">
          Manage haulier and warehouse rate cards.
        </p>
      </div>

      {/* ═══════ Haulier Rate Cards ═══════ */}
      <section className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Haulier Rate Cards
          </h2>
        </div>
        <div className="divide-y divide-gray-100">
          {HAULIER_ORDER.map((h) => (
            <HaulierCardPanel key={h} haulier={h} card={currentByHaulier[h]} />
          ))}
        </div>
        <div className="p-6 pt-4">
          {!showHaulierUpload ? (
            <button
              onClick={() => setShowHaulierUpload(true)}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
            >
              Upload New Rate Card
            </button>
          ) : (
            <div className="space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Haulier
                </label>
                <select
                  value={uploadHaulierType}
                  onChange={(e) => setUploadHaulierType(e.target.value as Haulier)}
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:w-64"
                >
                  {HAULIER_ORDER.map((h) => (
                    <option key={h} value={h}>
                      {HAULIER_LABELS[h]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  CSV File
                </label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setHaulierFile(e.target.files?.[0] ?? null)}
                  className="mt-1 block w-full text-sm text-gray-700 file:mr-4 file:rounded-md file:border-0 file:bg-blue-50 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-blue-700 hover:file:bg-blue-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Effective From
                </label>
                <input
                  type="date"
                  value={haulierEffective}
                  onChange={(e) => setHaulierEffective(e.target.value)}
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleHaulierUpload}
                  disabled={!haulierFile || !haulierEffective || uploadHaulier.isPending}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
                >
                  {uploadHaulier.isPending ? "Uploading..." : "Upload"}
                </button>
                <button
                  onClick={() => {
                    setShowHaulierUpload(false);
                    setHaulierFile(null);
                    setHaulierEffective("");
                    setUploadHaulierType(Haulier.PALLETWAYS);
                  }}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
                >
                  Cancel
                </button>
              </div>
              {uploadHaulier.isError && (
                <p className="text-sm text-red-600">
                  {uploadHaulier.error instanceof Error
                    ? uploadHaulier.error.message
                    : "Upload failed. Please try again."}
                </p>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ═══════ Warehouse Rates ═══════ */}
      <section className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Warehouse Rates
          </h2>
        </div>
        <div className="p-6">
          {currentWarehouse ? (
            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <p className="text-xs font-medium uppercase text-gray-500">
                    Effective From
                  </p>
                  <p className="mt-1 text-sm text-gray-900">
                    {formatDate(currentWarehouse.effective_from)}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase text-gray-500">
                    Upload Date
                  </p>
                  <p className="mt-1 text-sm text-gray-900">
                    {formatDate(currentWarehouse.uploaded_at)}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase text-gray-500">
                    Uploaded By
                  </p>
                  <p className="mt-1 text-sm text-gray-900">
                    {currentWarehouse.uploaded_by}
                  </p>
                </div>
              </div>

              {/* Current rates table — the warehouse rates are embedded on the card.
                  If the API provides them as a sub-field, display them. Otherwise
                  show a generic summary. */}
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50 text-xs font-medium uppercase tracking-wide text-gray-500">
                      <th className="px-4 py-2">Charge Type</th>
                      <th className="px-4 py-2">Unit Price</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {[ChargeType.PICK, ChargeType.RECEIPT, ChargeType.RENTAL].map(
                      (ct) => {
                        // Read warehouse_rates from the enriched rate card
                        const rates =
                          ((currentWarehouse as unknown as Record<string, unknown>).warehouse_rates as
                            | { charge_type: string; unit_price: number }[]
                            | undefined) ?? [];
                        const rate = rates.find((r) => r.charge_type === ct);
                        return (
                          <tr key={ct} className="hover:bg-gray-50">
                            <td className="px-4 py-2 font-medium text-gray-900">
                              {ct === ChargeType.PICK ? "Goods Out" : ct === ChargeType.RECEIPT ? "Goods In" : "Weekly Storage"}
                            </td>
                            <td className="px-4 py-2 text-gray-700">
                              {rate ? formatCurrency(rate.unit_price) : "—"}
                            </td>
                          </tr>
                        );
                      },
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">
              No warehouse rates configured yet.
            </p>
          )}

          <div className="mt-6">
            {!showWarehouseEdit ? (
              <button
                onClick={() => setShowWarehouseEdit(true)}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
              >
                Edit Rates
              </button>
            ) : (
              <div className="space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Goods Out (per pallet)
                    </label>
                    <div className="relative mt-1">
                      <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                        &pound;
                      </span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={pickRate}
                        onChange={(e) => setPickRate(e.target.value)}
                        placeholder="0.00"
                        className="block w-full rounded-lg border border-gray-300 py-2 pl-7 pr-3 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Goods In (per pallet)
                    </label>
                    <div className="relative mt-1">
                      <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                        &pound;
                      </span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={receiptRate}
                        onChange={(e) => setReceiptRate(e.target.value)}
                        placeholder="0.00"
                        className="block w-full rounded-lg border border-gray-300 py-2 pl-7 pr-3 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Weekly Storage (per pallet)
                    </label>
                    <div className="relative mt-1">
                      <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                        &pound;
                      </span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={rentalRate}
                        onChange={(e) => setRentalRate(e.target.value)}
                        placeholder="0.00"
                        className="block w-full rounded-lg border border-gray-300 py-2 pl-7 pr-3 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Effective From
                  </label>
                  <input
                    type="date"
                    value={warehouseEffective}
                    onChange={(e) => setWarehouseEffective(e.target.value)}
                    className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:w-auto"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleWarehouseSave}
                    disabled={!warehouseEffective || setWarehouse.isPending}
                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
                  >
                    {setWarehouse.isPending ? "Saving..." : "Save"}
                  </button>
                  <button
                    onClick={() => {
                      setShowWarehouseEdit(false);
                      setPickRate("");
                      setReceiptRate("");
                      setRentalRate("");
                      setWarehouseEffective("");
                    }}
                    className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                </div>
                {setWarehouse.isError && (
                  <p className="text-sm text-red-600">
                    {setWarehouse.error instanceof Error
                      ? setWarehouse.error.message
                      : "Failed to save rates. Please try again."}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ═══════ Rate Card History ═══════ */}
      <section className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Rate Card History
          </h2>
        </div>

        {allCards.length === 0 ? (
          <div className="px-6 py-8 text-center text-sm text-gray-500">
            No rate card history available.
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {allCards.map((card) => (
              <RateCardHistoryRow key={card.id} card={card} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// ─── Haulier current-card panel ────────────────────────────────────────────────

function HaulierCardPanel({ haulier, card }: { haulier: Haulier; card: RateCard | null }) {
  return (
    <div className="px-6 py-5">
      <h3 className="text-sm font-semibold text-gray-900">
        {HAULIER_LABELS[haulier]}
      </h3>
      {card ? (
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <p className="text-xs font-medium uppercase text-gray-500">
                Effective From
              </p>
              <p className="mt-1 text-sm text-gray-900">
                {formatDate(card.effective_from)}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase text-gray-500">
                Upload Date
              </p>
              <p className="mt-1 text-sm text-gray-900">
                {formatDate(card.uploaded_at)}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase text-gray-500">
                Uploaded By
              </p>
              <p className="mt-1 text-sm text-gray-900">
                {card.uploaded_by}
              </p>
            </div>
          </div>
          {card.file_key && (
            <button
              onClick={() => rateCards.download(card.id)}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
              Download original CSV
            </button>
          )}

          {haulier === Haulier.BOX_LOGISTICS && <BoxRatesTable rateCardId={card.id} />}
          {haulier === Haulier.TPN && <TpnRatesTable rateCardId={card.id} />}
        </div>
      ) : (
        <p className="mt-2 text-sm text-gray-500">
          No {HAULIER_LABELS[haulier]} rate card uploaded yet.
        </p>
      )}
    </div>
  );
}

// ─── Box Logistics rates table ─────────────────────────────────────────────────

function BoxRatesTable({ rateCardId }: { rateCardId: string }) {
  const { data, isLoading, error } = useRateCardRates(rateCardId);

  if (isLoading) {
    return <p className="mt-3 text-sm text-gray-500">Loading rates...</p>;
  }
  if (error) {
    return <p className="mt-3 text-sm text-red-600">Failed to load rates.</p>;
  }

  const bands = (data?.rates ?? []) as BoxBandRate[];

  type Row = { band: string; postcodes: string; service: string; qtr: number | null; half: number | null; full: number | null };
  const rows: Row[] = [];
  for (const band of bands) {
    const services = Object.keys(band.services).sort(
      (a, b) => BOX_SERVICE_ORDER.indexOf(a) - BOX_SERVICE_ORDER.indexOf(b),
    );
    for (const service of services) {
      const rate = band.services[service];
      rows.push({
        band: band.band,
        postcodes: band.postcodes,
        service,
        qtr: rate.qtr,
        half: rate.half,
        full: rate.full,
      });
    }
  }

  if (rows.length === 0) {
    return <p className="mt-3 text-sm text-gray-500">No rate rows found on this card.</p>;
  }

  return (
    <div className="mt-4 overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50 text-xs font-medium uppercase tracking-wide text-gray-500">
            <th className="px-3 py-2">Band</th>
            <th className="px-3 py-2">Postcodes</th>
            <th className="px-3 py-2">Service</th>
            <th className="px-3 py-2 text-right">Qtr</th>
            <th className="px-3 py-2 text-right">Half</th>
            <th className="px-3 py-2 text-right">Full</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((r, idx) => (
            <tr key={idx} className="hover:bg-gray-50">
              <td className="whitespace-nowrap px-3 py-1.5 font-medium text-gray-900">{r.band}</td>
              <td className="px-3 py-1.5 text-gray-700">{r.postcodes}</td>
              <td className="whitespace-nowrap px-3 py-1.5 text-gray-700">{r.service}</td>
              <td className="whitespace-nowrap px-3 py-1.5 text-right font-mono text-gray-700">{formatPoa(r.qtr)}</td>
              <td className="whitespace-nowrap px-3 py-1.5 text-right font-mono text-gray-700">{formatPoa(r.half)}</td>
              <td className="whitespace-nowrap px-3 py-1.5 text-right font-mono text-gray-700">{formatPoa(r.full)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── TPN rates table ────────────────────────────────────────────────────────────

const TPN_DISPLAY_LIMIT = 100;

function TpnRatesTable({ rateCardId }: { rateCardId: string }) {
  const { data, isLoading, error } = useRateCardRates(rateCardId);
  const [prefix, setPrefix] = useState("");

  if (isLoading) {
    return <p className="mt-3 text-sm text-gray-500">Loading rates...</p>;
  }
  if (error) {
    return <p className="mt-3 text-sm text-red-600">Failed to load rates.</p>;
  }

  const allRows = (data?.rates ?? []) as TpnRate[];
  const needle = prefix.trim().toUpperCase();
  const filtered = needle
    ? allRows.filter((r) => r.postcode_district.toUpperCase().startsWith(needle))
    : allRows;
  const rows = filtered.slice(0, TPN_DISPLAY_LIMIT);

  return (
    <div className="mt-4">
      <div className="mb-2 flex items-center gap-2">
        <input
          type="text"
          value={prefix}
          onChange={(e) => setPrefix(e.target.value)}
          placeholder="Filter by postcode prefix, e.g. NW"
          className="w-64 rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <span className="text-xs text-gray-500">
          {filtered.length.toLocaleString()} of {allRows.length.toLocaleString()} rows
          {filtered.length > TPN_DISPLAY_LIMIT ? ` — showing first ${TPN_DISPLAY_LIMIT}` : ""}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-xs font-medium uppercase tracking-wide text-gray-500">
              <th className="px-3 py-2">Postcode</th>
              <th className="px-3 py-2">Zone</th>
              <th className="px-3 py-2 text-right">Econ Qtr</th>
              <th className="px-3 py-2 text-right">Econ Half</th>
              <th className="px-3 py-2 text-right">Econ Light</th>
              <th className="px-3 py-2 text-right">Econ Full</th>
              <th className="px-3 py-2 text-right">ND Qtr</th>
              <th className="px-3 py-2 text-right">ND Half</th>
              <th className="px-3 py-2 text-right">ND Light</th>
              <th className="px-3 py-2 text-right">ND Full</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length === 0 && (
              <tr>
                <td colSpan={10} className="px-3 py-6 text-center text-gray-400">
                  No postcode districts match "{prefix}".
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-gray-50">
                <td className="whitespace-nowrap px-3 py-1.5 font-medium text-gray-900">{r.postcode_district}</td>
                <td className="whitespace-nowrap px-3 py-1.5 text-gray-700">{r.zone}</td>
                <td className="whitespace-nowrap px-3 py-1.5 text-right font-mono text-gray-700">{formatDash(r.econ_qtr)}</td>
                <td className="whitespace-nowrap px-3 py-1.5 text-right font-mono text-gray-700">{formatDash(r.econ_half)}</td>
                <td className="whitespace-nowrap px-3 py-1.5 text-right font-mono text-gray-700">{formatDash(r.econ_light)}</td>
                <td className="whitespace-nowrap px-3 py-1.5 text-right font-mono text-gray-700">{formatDash(r.econ_full)}</td>
                <td className="whitespace-nowrap px-3 py-1.5 text-right font-mono text-gray-700">{formatDash(r.nd_qtr)}</td>
                <td className="whitespace-nowrap px-3 py-1.5 text-right font-mono text-gray-700">{formatDash(r.nd_half)}</td>
                <td className="whitespace-nowrap px-3 py-1.5 text-right font-mono text-gray-700">{formatDash(r.nd_light)}</td>
                <td className="whitespace-nowrap px-3 py-1.5 text-right font-mono text-gray-700">{formatDash(r.nd_full)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── History Row Sub-component ────────────────────────────────────────────────

function RateCardHistoryRow({ card }: { card: RateCard }) {
  const isHaulier = card.card_type === RateCardType.HAULIER;
  const label = isHaulier ? HAULIER_LABELS[cardHaulier(card)] : "Warehouse";

  return (
    <div className="flex items-center justify-between px-6 py-4">
      <div className="flex items-center gap-4">
        {/* Timeline dot */}
        <div
          className={`h-3 w-3 rounded-full ${
            isHaulier ? "bg-blue-500" : "bg-purple-500"
          }`}
        />
        <div>
          <p className="text-sm font-medium text-gray-900">
            {formatDate(card.effective_from)}
            {card.effective_to ? ` — ${formatDate(card.effective_to)}` : " — Present"}
          </p>
          <p className="text-xs text-gray-500">
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                isHaulier
                  ? "bg-blue-100 text-blue-800"
                  : "bg-purple-100 text-purple-800"
              }`}
            >
              {label}
            </span>
            <span className="ml-2">by {card.uploaded_by}</span>
          </p>
        </div>
      </div>
      {isHaulier && card.file_key && (
        <button
          onClick={() => rateCards.download(card.id)}
          className="text-sm font-medium text-blue-600 hover:text-blue-700"
        >
          Download
        </button>
      )}
    </div>
  );
}
